import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import SuccessPopup from '../shared/SuccessPopup';
import AutoSaveToast from '../shared/AutoSaveToast';
import { confirmUngeocodedMapAddressIfNeeded } from '../../utils/mapSubmitGuard';
import { mapFieldHasPlacedMarker } from '../../utils/mapFieldValidation';
import { combineFormInstances, splitFormDataByInstance, extractStudentInfo } from '../../utils/formCombiner';
import { syncSharedFields } from '../../utils/sharedFieldMapper';
import '../superadmin/AddDistrict.css';

const ReturningStudentForm = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [combinedFormDefinition, setCombinedFormDefinition] = useState(null);
  const [formInstanceMap, setFormInstanceMap] = useState({});
  const [formInstances, setFormInstances] = useState([]);
  const [allFormInstances, setAllFormInstances] = useState([]); // Store all instances before filtering
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [saveFeedbackModal, setSaveFeedbackModal] = useState(null);
  const [autoSaveToast, setAutoSaveToast] = useState(null);
  const [student, setStudent] = useState(null);
  const previousFormValuesRef = useRef({});

  // Calculate form completion percentage
  const calculateProgress = useCallback(() => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return 0;
    }

    let totalFields = 0;
    let filledFields = 0;

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      section.fields.forEach(field => {
        if (field.admin_only) return;

        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          const currentValue = formValues[conditionalField];
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        totalFields++;
        const value = formValues[field.name];

        if (field.type === 'checkbox') {
          if (value && Array.isArray(value) && value.length > 0) {
            filledFields++;
          }
        } else if (value && (typeof value === 'string' && value.trim() !== '')) {
          filledFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  }, [combinedFormDefinition, formValues]);

  // Save progress to backend
  const saveProgress = useCallback(async (options = {}) => {
    const trigger = options.trigger === 'manual' ? 'manual' : 'auto';
    if (savingProgress) return;

    const progressPercentage = calculateProgress();
    const hasUserInput = Object.values(formValues).some(
      (value) => value !== '' && (!Array.isArray(value) || value.length > 0)
    );

    if (!hasUserInput && progressPercentage === 0) {
      console.log('ReturningStudentForm: Skipping save - no user input yet');
      return;
    }

    try {
      setSavingProgress(true);

      await axios.post('/api/form-submissions/save-progress', {
        submission_data: formValues,
        progress_percentage: progressPercentage,
        form_type: 'returning_student',
        student_id: studentId
      });

      setLastSavedTime(new Date());

      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'success',
          title: 'Progress saved',
          message: 'Your form progress has been saved.'
        });
      } else {
        setAutoSaveToast({ variant: 'success', message: 'Progress saved automatically.' });
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
      const msg =
        error.response?.data?.error || error.message || 'Could not save progress. Please try again.';
      if (trigger === 'manual') {
        setSaveFeedbackModal({
          variant: 'error',
          title: 'Save failed',
          message: msg
        });
      } else {
        setAutoSaveToast({ variant: 'error', message: 'Could not save progress automatically.' });
      }
    } finally {
      setSavingProgress(false);
    }
  }, [formValues, savingProgress, calculateProgress, studentId]);

  useEffect(() => {
    if (studentId) {
      fetchStudentAndForms();
    }
  }, [studentId]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    if (Object.keys(formValues).length === 0) return;

    const autoSaveTimer = setTimeout(() => {
      saveProgress({ trigger: 'auto' });
    }, 30000); // 30 seconds

    return () => clearTimeout(autoSaveTimer);
  }, [formValues, saveProgress]);

  const fetchStudentAndForms = async () => {
    try {
      setLoading(true);

      // Fetch student info
      const studentResponse = await axios.get(`/api/students/my-students/${studentId}`);
      setStudent(studentResponse.data);

      // Fetch available forms
      const response = await axios.get('/api/form-instances/parent');

      if (response.data && response.data.length > 0) {
        // Store all instances before filtering
        setAllFormInstances(response.data);

        // Filter out the excluded forms for returning students
        const filteredInstances = response.data.filter(instance => {
          const formName = (instance.instance_name || instance.master_form_name || '').toLowerCase();

          // Exclude: Helper form, Residency Determination form
          const excludedForms = [
            'helper form',
            'residency determination',
            'residency verification'
          ];

          return !excludedForms.some(excluded =>
            formName.includes(excluded) || excluded.includes(formName)
          );
        });

        console.log('Available forms after filtering for returning students:', filteredInstances.map(f => f.instance_name));

        if (filteredInstances.length === 0) {
          setError('No forms available for returning students');
          setLoading(false);
          return;
        }

        setFormInstances(filteredInstances);

        // Combine all form instances into one
        const combined = combineFormInstances(filteredInstances);
        if (combined) {
          setCombinedFormDefinition(combined);
          setFormInstanceMap(combined.formInstanceMap);
          // Initialize previous values ref with empty object
          previousFormValuesRef.current = {};
        } else {
          setError('Failed to combine form instances');
        }
      } else {
        setError('No active forms found for your district');
      }
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      setError('Failed to load forms. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load saved progress when forms are ready
  useEffect(() => {
    if (combinedFormDefinition && student) {
      loadSavedProgress();
    }
  }, [combinedFormDefinition, student]);

  const loadSavedProgress = async () => {
    try {
      const response = await axios.get('/api/form-submissions/progress');

      // Check if there's saved data for returning student forms
      if (response.data &&
          response.data.submission_data &&
          Object.keys(response.data.submission_data).length > 0) {
        const savedData = response.data.submission_data || {};
        setFormValues(savedData);
        previousFormValuesRef.current = savedData;
      } else {
        // No saved progress, initialize with existing student data
        initializeWithStudentData();
      }
    } catch (error) {
      console.error('Failed to load saved progress:', error);
      // Initialize with student data anyway
      initializeWithStudentData();
    }
  };

  // Initialize form with existing student data from previous submissions
  const initializeWithStudentData = () => {
    if (!student) return;

    // For returning students, we should load data from their previous new student submissions
    // This is more complex - we need to fetch their previous submission data
    loadExistingSubmissionData();
  };

  const loadExistingSubmissionData = async () => {
    try {
      console.log('Loading existing submission data for student:', studentId);

      // Get all the student's previous submissions
      const submissionsResponse = await axios.get(`/api/form-submissions/my-student/${studentId}`);
      console.log('Previous submissions for student:', submissionsResponse.data);

      // For each form instance in our combined form, try to find existing data
      const populatedData = {};

      formInstances.forEach(formInstance => {
        console.log('Checking form instance:', formInstance.instance_name, formInstance.id);

        // Find submission for this form instance
        const submission = submissionsResponse.data.find(sub =>
          sub.form_instance_id === formInstance.id
        );

        if (submission && submission.form_data) {
          console.log('Found submission data for', formInstance.instance_name, ':', submission.form_data);

          // Add the data with proper prefixes for the combined form
          Object.keys(submission.form_data).forEach(fieldName => {
            const prefixedFieldName = `${formInstance.id}_${fieldName}`;
            populatedData[prefixedFieldName] = submission.form_data[fieldName];
          });
        } else {
          console.log('No submission data found for', formInstance.instance_name);
        }
      });

      // Also try to load from general progress data (in case there was incomplete work)
      try {
        const progressResponse = await axios.get('/api/form-submissions/progress');
        if (progressResponse.data && progressResponse.data.submission_data) {
          console.log('Also loading from progress data:', progressResponse.data.submission_data);
          // Merge progress data (progress data takes precedence)
          Object.assign(populatedData, progressResponse.data.submission_data);
        }
      } catch (progressError) {
        console.log('No progress data found');
      }

      // If we found any data, use it
      if (Object.keys(populatedData).length > 0) {
        console.log('Populated form with existing data:', populatedData);
        setFormValues(populatedData);
        previousFormValuesRef.current = populatedData;
      } else {
        console.log('No existing submission data found, starting fresh');
        // Initialize with basic student info from the student record
        const basicData = {};
        if (student) {
          // Try to map basic student info to form fields
          // This is a fallback - ideally we'd have the data from submissions
          console.log('Initializing with basic student data as fallback');
        }
      }
    } catch (error) {
      console.error('Failed to load existing submission data:', error);
      // Continue without existing data
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return errors;
    }

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      section.fields.forEach(field => {
        if (field.admin_only) return;

        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          const currentValue = formValues[conditionalField];
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        const isRequired = field.required || (field.conditional_required && isVisible);
        const value = formValues[field.name];

        if (isRequired) {
          // Handle different field types
          if (field.type === 'checkbox') {
            if (!value || !Array.isArray(value) || value.length === 0) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'signature') {
            // Signature fields can be string (old format) or object with image property
            if (!value ||
                (typeof value === 'string' && value.trim() === '') ||
                (typeof value === 'object' && !value.image)) {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'date') {
            if (!value || value.trim() === '') {
              errors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'map') {
            if (!mapFieldHasPlacedMarker(value)) {
              errors[field.name] =
                `${field.label}: place a marker on the map (use Find on Map or click the map).`;
            }
          } else if (!value || (typeof value === 'string' && value.trim() === '')) {
            errors[field.name] = `${field.label} is required`;
          }
        }

        if (field.type === 'phone') {
          const phoneValue = formValues[field.name];
          if (phoneValue && typeof phoneValue === 'string') {
            const digits = phoneValue.replace(/\D/g, '');
            if (digits.length > 0 && digits.length !== 10) {
              errors[field.name] = 'Please enter a valid phone number';
            }
          }
        }
      });
    });

    return errors;
  };

  // Check if form is complete (all required fields filled)
  const isFormComplete = useCallback(() => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
  }, [formValues, combinedFormDefinition]);

  // Get form completion status
  const getFormCompletionStatus = () => {
    if (!combinedFormDefinition || !combinedFormDefinition.sections) {
      return { isComplete: false, missingFields: [] };
    }

    const missingFields = [];

    combinedFormDefinition.sections.forEach(section => {
      if (section.admin_only || section.is_form_header) return;

      section.fields.forEach(field => {
        if (field.admin_only) return;

        // Check visibility
        let isVisible = true;
        if (field.conditional) {
          const conditionalField = field.conditional.field;
          const conditionalValue = field.conditional.value;
          const currentValue = formValues[conditionalField];
          isVisible = Array.isArray(conditionalValue)
            ? conditionalValue.includes(currentValue)
            : conditionalValue === currentValue;
        }

        if (!isVisible) return;

        // Check required
        const isRequired = field.required ||
          (field.conditional_required && isVisible);

        if (isRequired) {
          const value = formValues[field.name];
          let isEmpty = false;

          if (field.type === 'checkbox') {
            isEmpty = !value || !Array.isArray(value) || value.length === 0;
          } else if (field.type === 'signature') {
            isEmpty = !value ||
              (typeof value === 'string' && value.trim() === '') ||
              (typeof value === 'object' && !value.image);
          } else if (field.type === 'date') {
            isEmpty = !value || value.trim() === '';
          } else if (field.type === 'map') {
            isEmpty = !mapFieldHasPlacedMarker(value);
          } else {
            isEmpty = !value || (typeof value === 'string' && value.trim() === '');
          }

          if (isEmpty) {
            missingFields.push(field.label);
          }
        }
      });
    });

    return {
      isComplete: missingFields.length === 0,
      missingFields
    };
  };

  const handleFormChange = (newValues) => {
    // Detect which field(s) changed by comparing with previous values
    const previousValues = previousFormValuesRef.current;
    const changedFields = [];

    // Find all changed fields
    Object.keys(newValues).forEach(fieldName => {
      const oldValue = previousValues[fieldName];
      const newValue = newValues[fieldName];

      // Compare values (handle arrays for checkboxes)
      if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        if (JSON.stringify(oldValue.sort()) !== JSON.stringify(newValue.sort())) {
          changedFields.push(fieldName);
        }
      } else if (oldValue !== newValue) {
        changedFields.push(fieldName);
      }
    });

    // Also check for removed fields
    Object.keys(previousValues).forEach(fieldName => {
      if (!(fieldName in newValues)) {
        changedFields.push(fieldName);
      }
    });

    // Sync shared fields for each changed field (only if form instances are loaded)
    let syncedValues = { ...newValues };
    if (formInstances.length > 0 && Object.keys(formInstanceMap).length > 0) {
      changedFields.forEach(changedFieldName => {
        const newValue = newValues[changedFieldName];
        const syncUpdates = syncSharedFields(
          changedFieldName,
          newValue,
          syncedValues,
          formInstanceMap,
          formInstances
        );

        // Merge sync updates into syncedValues
        syncedValues = {
          ...syncedValues,
          ...syncUpdates
        };
      });
    }

    // Update form values with synced values
    setFormValues(syncedValues);

    // Update ref for next comparison
    previousFormValuesRef.current = syncedValues;

    // Clear validation errors when user makes changes
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({});
    }
  };

  const handleDeleteProgress = async () => {
    try {
      await axios.delete('/api/form-submissions/progress');
      setFormValues({});
      setShowDeleteConfirm(false);
      setSuccess(false);
      setError('');
      // Navigate away from the form
      navigate('/parent/students');
    } catch (error) {
      console.error('Failed to delete progress:', error);
      setError('Failed to delete form progress');
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const completionStatus = getFormCompletionStatus();
      const errorMessage = completionStatus.missingFields.length > 0
        ? `Please complete all required fields. Missing: ${completionStatus.missingFields.slice(0, 5).join(', ')}${completionStatus.missingFields.length > 5 ? '...' : ''}`
        : 'Please fill in all required fields';
      setError(errorMessage);

      const firstErrorField = Object.keys(errors)[0];
      const mapBtn = document.getElementById(`map-find-on-map-${firstErrorField}`);
      const fieldWrap = document.getElementById(`field-${firstErrorField}`);
      if (mapBtn || fieldWrap) {
        if (fieldWrap) fieldWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (mapBtn) setTimeout(() => mapBtn.focus(), 300);
      } else {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }

      return;
    }

    if (combinedFormDefinition && !confirmUngeocodedMapAddressIfNeeded(combinedFormDefinition, formValues)) {
      return;
    }

    setLoading(true);

    try {
      // Extract student info from form data
      const studentInfo = extractStudentInfo(formValues, formInstanceMap, formInstances);

      if (!studentInfo || !studentInfo.first_name || !studentInfo.last_name) {
        setError('Student name is required. Please fill in the student information section.');
        setLoading(false);
        return;
      }

      // Split form data by instance
      const splitData = splitFormDataByInstance(formValues, formInstanceMap, formInstances);

      // Prepare form_instances_data for submission
      const formInstancesData = {};
      formInstances.forEach(instance => {
        const instanceData = splitData[instance.id];
        if (instanceData) {
          formInstancesData[instance.id] = {
            student_name: `${studentInfo.first_name} ${studentInfo.last_name}`,
            data: instanceData,
            allows_multiple: instance.form_data?.allows_multiple || false
          };
        }
      });

      // Submit to form-submissions endpoint (which will also update student)
      await axios.post('/api/form-submissions/submit', {
        submission_data: formValues,
        form_instances_data: formInstancesData,
        student_info: studentInfo,
        form_type: 'returning_student' // Mark as returning student form
      });

      // Clear saved progress after successful submission
      try {
        await axios.post('/api/form-submissions/save-progress', {
          submission_data: {},
          progress_percentage: 0
        });
      } catch (error) {
        console.error('Failed to clear saved progress:', error);
      }

      setSuccess(true);
      
      // Navigate with success message
      const studentName = `${studentInfo.first_name} ${studentInfo.last_name}`;
      navigate(`/parent/student-intake?success=true&studentName=${encodeURIComponent(studentName)}`);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>Returning Student Form</h2>
        <div>Loading forms...</div>
      </div>
    );
  }

  if (!combinedFormDefinition) {
    return (
      <div className="add-district">
        <h2>Returning Student Form</h2>
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div>No forms available</div>
        )}
      </div>
    );
  }

  return (
    <div className="add-district">
      <h2>Returning Student Information Update</h2>
      {student && (
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          Updating information for: <strong>{student.first_name} {student.last_name}</strong>
        </p>
      )}

      {/* Progress Bar with Save Button */}
      <div style={{
        background: '#ff9800',
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong>Form Progress:</strong> {calculateProgress()}% complete
          {lastSavedTime && (
            <span style={{ fontSize: '0.8em', opacity: 0.9, marginLeft: '1rem' }}>
              • Last saved: {lastSavedTime.toLocaleTimeString()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => saveProgress({ trigger: 'manual' })}
          disabled={savingProgress}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid white',
            padding: '0.4rem 0.8rem',
            borderRadius: '4px',
            cursor: savingProgress ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem'
          }}
        >
          {savingProgress ? 'Saving...' : 'Save Progress'}
        </button>
      </div>

      {savingProgress && (
        <div style={{ background: '#e3f2fd', color: '#1976d2', padding: '0.5rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          Saving progress...
        </div>
      )}

      <SuccessPopup
        show={!!saveFeedbackModal}
        onClose={() => setSaveFeedbackModal(null)}
        variant={saveFeedbackModal?.variant || 'success'}
        title={saveFeedbackModal?.title}
        message={saveFeedbackModal?.message || ''}
        autoCloseDelay={saveFeedbackModal?.variant === 'error' ? 0 : 5000}
      />
      <AutoSaveToast
        open={!!autoSaveToast}
        onClose={() => setAutoSaveToast(null)}
        variant={autoSaveToast?.variant || 'success'}
        message={autoSaveToast?.message || ''}
        duration={5000}
      />
      {error && <div className="error-message">{error}</div>}

      {success && (
        <div style={{ background: '#dfd', color: '#3a3', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          Student information updated successfully!
        </div>
      )}

      {(() => {
        const completionStatus = getFormCompletionStatus();
        if (!completionStatus.isComplete && Object.keys(formValues).length > 0) {
          return (
            <div style={{
              background: '#fff3cd',
              color: '#856404',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #ffc107'
            }}>
              <strong>Form Incomplete:</strong> Please fill in all required fields before submitting.
              {completionStatus.missingFields.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                  Missing fields: {completionStatus.missingFields.slice(0, 3).join(', ')}
                  {completionStatus.missingFields.length > 3 && ` and ${completionStatus.missingFields.length - 3} more...`}
                </div>
              )}
            </div>
          );
        }
        return null;
      })()}

      <form onSubmit={handleSubmit}>
        <FormRenderer
          formData={combinedFormDefinition}
          formValues={formValues}
          onChange={handleFormChange}
          errors={validationErrors}
          hideFormYear
        />

        <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !isFormComplete()}
              title={!isFormComplete() ? 'Please complete all required fields before submitting' : ''}
            >
              {loading ? 'Submitting...' : 'Submit Forms'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                await saveProgress({ trigger: 'manual' });
                navigate('/parent/students');
              }}
            >
              Save for Later
            </button>
          </div>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
            style={{ background: '#dc3545', color: 'white', border: 'none' }}
          >
            Cancel Form
          </button>
        </div>

        {showDeleteConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Delete Form Progress?</h3>
              <p style={{ marginBottom: '1rem' }}>
                Are you sure you want to delete all your form progress? This action cannot be undone.
              </p>
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                  <strong>Tip:</strong> If you want to save your progress and continue later,
                  just close this page or click "Save for Later". Your progress is automatically saved
                  every 30 seconds and will be available when you return.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                >
                  Keep Progress
                </button>
                <button
                  onClick={handleDeleteProgress}
                  style={{ background: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Delete Progress
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ReturningStudentForm;

